import * as vp from 'vopay';
import React, { ReactNode, createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDataDog } from './Datadog.provider';
import useStorage from '../hooks/useStorage';
import useFetchPaylinkSingleUseToken from '../hooks/useApi/useFetchPaylinkSingleUseToken';
import { useToken } from './Token.provider';
import { useDataFetchIndicator } from '../hooks';

export enum Flow {
  NONE = 0,
  WITHDRAW__SINGLE = 1,
  WITHDRAW__MULTIPLE_PAYMENTS = 2,
  FUND__SINGLE = 3,
  FUND__SCHEDULED = 4,
  CONNECT = 5,
}

type PaylinkCtx = {
  paylink: vp.ELinxDetails;
  flow: Flow;
  Token: string;
  isInitalized: boolean;
  paylinkSuccess: boolean;
  refetchElinx: () => Promise<void>;
};

const elinxDefaults: vp.ELinxDetails = {
  FirstName: '',
  LastName: '',
  ReceiverName: '',
  SenderEmailAddress: '',
  ReceiverEmailAddress: '',
  InvoiceNumber: '',
  Note: '',
  SenderName: '',
  Amount: '',
  Currency: '',
  Status: '',
  FailureReason: '',
  Link: '',
  IframeURL: '',
  IframeKey: '',
  TransactionID: '',
  ScheduledTransactionID: '',
  EFTType: '',
  PaymentType: '',
  TransactionDate: '',
  Frequency: '',
  NameOfFrequency: '',
  SemiMonthlyFrequencyType: '',
  ScheduleEndDate: '',
  EndingAfterPayments: '',
  Type: '',
  CanDecline: '',
  ScheduleStartDate: '',
  LastModified: '',
  BankAccountLinked: false,
  BankAccountToken: '',
  ExpireDate: 30,
  RecipientMessage: '',
  FlexiblePaymentOptions: '',
  MinimumPaymentAccepted: '',
  RemainderPaymentDate: '',
  ReceiverPhoneNumber: '',
  PaymentMethodDisplayOptions: 'New',
  ClientAccountID: '',
  DisplayEmbbedInterac: false,
  BankAccountRail: ['eft'],
  DocumentLink: '',
};

export const PaylinkContext = createContext<PaylinkCtx>({
  paylink: elinxDefaults,
  paylinkSuccess: false,
  flow: Flow.NONE,
  Token: '',
  isInitalized: false,
  refetchElinx: async () => {},
});

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const PaylinkProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const params = useQuery();
  const storage = useStorage();
  const { setToken } = useToken();
  const [paylink, , , fetchSingleUseTokenInfoAsync] = useFetchPaylinkSingleUseToken({});
  const { clearPaylinkToken, getPaylinkToken } = storage.paylinkToken;
  const { clearBank } = storage.bank;
  const { clearAccessTokenFields } = storage.accessToken;
  const { clearIQ11Token } = storage.token;
  const { clearJWT } = storage.JWT;
  const { clearPadInfo } = storage.padAgreement;
  const { clearPaymentMethod } = storage.paymentMethod;
  const [flow, setFlow] = useState<Flow>(Flow.NONE);
  const { Token } = getPaylinkToken();
  const [tempToken, setTempToken] = useState('');
  const [isInitalized, setIsInitialized] = useState(false);
  const { setUser, setService } = useDataDog();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Common' });
  const [, setLoading] = useDataFetchIndicator();
  useEffect(() => {
    // prevent redirect issue for url in production
    const debounceTimer = setTimeout(() => {
      const urlToken = params.get('Token');
      if (urlToken) {
        clearPaylinkToken();
        clearBank();
        clearAccessTokenFields();
        clearIQ11Token();
        clearJWT();
        clearPadInfo();
        clearPaymentMethod();
      }

      const tokenToUse = urlToken || Token;
      if (urlToken) {
        storage.paylinkToken.storePaylinkToken(urlToken);
      }
      setTempToken(tokenToUse);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [params]);

  useEffect(() => {
    setIsInitialized(false);

    if (!tempToken) {
      setIsInitialized(true);
      return;
    }

    setService('elinx-request');
    setUser({ Token: tempToken });

    const abortController = new AbortController();

    const fetchPaylinkInfo = async () => {
      setLoading(true, `${t('defaultLoading')}`, 'fullScreen');

      try {
        const result = await fetchSingleUseTokenInfoAsync({ Token: tempToken, ApplicationType: 'paylink' });

        if (abortController.signal.aborted) return;

        if (!result.Success) {
          history.push('/error');
          return;
        }

        vp.setTitle(result.PageTitle ?? 'eLinx');
        vp.setFavicon(result.Favicon ?? 'src/assets/images/favicon/elinx-favicon.png');
        setToken(result.ExtraInfo.PayLinkDetails.IframeKey);

        const { PaymentType, Type, Frequency } = result.ExtraInfo.PayLinkDetails;

        if (PaymentType === 'withdraw') {
          if (Type === 'transaction' || (Type === 'scheduled' && Frequency === 'single')) {
            setFlow(Flow.WITHDRAW__SINGLE);
          } else if (Type === 'scheduled' || Frequency === 'recurring') {
            setFlow(Flow.WITHDRAW__MULTIPLE_PAYMENTS);
          } else {
            setFlow(Flow.CONNECT);
          }
        } else if (PaymentType === 'fund') {
          if (Type === 'transaction' || Frequency === 'single') {
            setFlow(Flow.FUND__SINGLE);
          } else if (Type === 'scheduled') {
            setFlow(Flow.FUND__SCHEDULED);
          } else {
            setFlow(Flow.CONNECT);
          }
        } else {
          setFlow(Flow.CONNECT);
        }

        setIsInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    void fetchPaylinkInfo();

    return () => {
      abortController.abort();
    };
  }, [tempToken]);

  const refetchElinx = useCallback(async () => {
    if (!tempToken) return;
    setLoading(true, `${t('defaultLoading')}`, 'fullScreen');
    try {
      await fetchSingleUseTokenInfoAsync({ Token: tempToken, ApplicationType: 'paylink' });
    } finally {
      setLoading(false);
    }
  }, [tempToken]);

  return (
    <PaylinkContext.Provider
      value={{
        paylink: paylink.Success ? paylink.ExtraInfo.PayLinkDetails : elinxDefaults,
        flow,
        Token: tempToken,
        isInitalized,
        paylinkSuccess: paylink.Success || false,
        refetchElinx,
      }}
    >
      {children}
    </PaylinkContext.Provider>
  );
};

export const usePaylink = (): PaylinkCtx => useContext(PaylinkContext);
